import React from "react";
import { NavLink } from "react-router-dom";
import { Box, Flex, Heading, Icon, useColorMode, Text } from "@chakra-ui/react";
import {
  FaMoon,
  FaSignOutAlt,
  FaSun,
  FaUser,
  FaBoxOpen,
  FaSignInAlt,
} from "react-icons/fa";

import { useAuth } from "@hooks";
import { NavButton } from "./NavButton";

type NavBarProps = {
  showLoggedInText: boolean;
};

export function NavBar({ showLoggedInText }: NavBarProps) {
  const { colorMode, toggleColorMode } = useColorMode();
  const { user, isLoggedIn, logout, goToLogin } = useAuth();

  const userDisplayName =
    user?.firebaseUser?.displayName ||
    (user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.firebaseUser?.email);

  const headerFontSize = "xl";

  return (
    <Flex
      as="header"
      shadow="md"
      transition="background-color 0.2s"
      pos="sticky"
      top="0"
      zIndex="3"
      left="0"
      right="0"
      bg="white"
      _dark={{ bg: "gray.800" }}
      width="full"
      padding="16px"
      alignItems="center"
    >
      <Flex alignItems="center">
        {" "}
        {/* Wrap heading and text in a Flex for alignment */}
        <Heading as={NavLink} to="/" mr={4} size={headerFontSize}>
          {" "}
          {/* Added mr={4} for spacing */}
          Collider Parts Database
        </Heading>
        {/* Conditional display of "User is logged in" text */}
        {isLoggedIn && userDisplayName && showLoggedInText && (
          <Text
            as="span" // Render as a span to be inline with the heading
            fontSize="3xl" // Adjust font size as desired, matching "xl" or larger
            fontWeight="bold"
            color="red.500" // Example color
            ml={4} // Margin left to separate from main heading
          >
            {userDisplayName} is logged in
          </Text>
        )}
      </Flex>
      <Box flex="1" /> {/* Spacer */}
      {/* Goto User Page */}
      {user && user.firebaseUser ? (
        <NavButton
          navType="link"
          link={`/users/${user.firebaseUser.uid}`}
          helperText="User Page"
        >
          <Icon as={FaUser} size="1rem" />
        </NavButton>
      ) : undefined}
      {/* Toggle Color Theme */}
      <NavButton
        navType="button"
        onClick={toggleColorMode}
        helperText={`Apply ${colorMode} Color Theme`}
      >
        {colorMode === "light" ? <Icon as={FaSun} size="1rem" /> : undefined}
        {colorMode === "dark" ? <Icon as={FaMoon} size="1rem" /> : undefined}
      </NavButton>
      {/* New Module */}
      <NavButton
        navType="link"
        link="/modules/new"
        helperText="Start a new module"
      >
        <Icon size="1rem" as={FaBoxOpen} />
      </NavButton>
      {/* LogIn / Logout */}
      {isLoggedIn ? (
        <NavButton navType="button" helperText="Logout" onClick={logout}>
          {user?.firstName} {user?.lastName} &nbsp;
          <Icon as={FaSignOutAlt} size="1rem" />
        </NavButton>
      ) : (
        <NavButton navType="button" helperText="LogIn" onClick={goToLogin}>
          <Icon as={FaSignInAlt} size="1rem" />
        </NavButton>
      )}
    </Flex>
  );
}
